/** Bounded ICU subset used by Faray catalogs: arguments, number, cardinal plural and b/u.
 * Values are appended as opaque text, never parsed as ICU or HTML. */
export function parseMessage(source) {
  if (typeof source !== 'string' || source.length > 32768) throw Error('Invalid message');
  let i = 0, nodes = 0;
  const token = () => {const m = /^[a-zA-Z0-9_=]+/.exec(source.slice(i)); if (!m) throw Error('Expected token'); i += m[0].length; return m[0];};
  const space = () => {while (/\s/.test(source[i] || '') && i < source.length) i++;};
  const take = c => {space(); if (source[i++] !== c) throw Error('Invalid message delimiter');};
  function sequence(end = '', depth = 0, plural = false) {
    if (depth > 12) throw Error('Message too deep');
    const result = []; let text = '';
    const flush = () => {if (text) {result.push({kind:'text', value:text}); text = '';}};
    while (i < source.length) {
      if (end && source.startsWith(end, i)) {flush(); i += end.length; return result;}
      const c = source[i++];
      if (++nodes > 32768) throw Error('Message too complex');
      if (c === "'") {
        if (source[i] === "'") {text += "'"; i++;}
        else if (['{','}','<','>'].includes(source[i]) || (plural && source[i] === '#')) {
          let closed = false;
          while (i < source.length) {const q = source[i++]; if (q === "'") {if (source[i] === "'") {text += "'"; i++;} else {closed = true; break;}} else text += q;}
          if (!closed) throw Error('Unclosed quote');
        } else text += c;
      } else if (c === '{') {
        flush(); space(); const name = token(); space();
        if (source[i] === '}') {i++; result.push({kind:'arg', name}); continue;}
        take(','); space(); const kind = token();
        if (kind === 'number') {take('}'); result.push({kind, name});}
        else if (kind === 'plural') {
          take(','); const options = {};
          while (true) {space(); if (source[i] === '}') {i++; break;} const category = token();
            if (!/^(zero|one|two|few|many|other|=\d+)$/.test(category) || Object.hasOwn(options, category)) throw Error('Invalid plural branch');
            take('{'); options[category] = sequence('}', depth + 1, true);
          }
          if (!options.other) throw Error('Missing other'); result.push({kind,name,options});
        } else throw Error('Unsupported ICU type');
      } else if (c === '<') {
        flush(); const name = token(); if (!['b','u'].includes(name) || source[i++] !== '>') throw Error('Unsupported tag');
        result.push({kind:'tag', name, children:sequence(`</${name}>`, depth + 1, plural)});
      } else if (c === '}' || c === '>') throw Error('Unexpected delimiter');
      else if (c === '#' && plural) {flush(); result.push({kind:'pound'});}
      else text += c;
    }
    if (end) throw Error('Unclosed message'); flush(); return result;
  }
  return sequence();
}
function signature(nodes) {
  const args = {}, tags = {};
  function visit(list) {for (const n of list) {
    if (n.name && n.kind !== 'tag') {const type = n.kind === 'arg' ? 'value' : 'number'; if (args[n.name] && args[n.name] !== type) throw Error('Mixed argument use'); args[n.name] = type;}
    if (n.kind === 'tag') {tags[n.name] = (tags[n.name] || 0) + 1; visit(n.children);}
    if (n.kind === 'plural') {
      let shape;
      for (const child of Object.values(n.options)) {const s = signature(child); const stamp = JSON.stringify(s); if (shape && stamp !== shape) throw Error('Branch argument mismatch'); shape = stamp;}
      // Category counts vary across languages; inspect one representative branch.
      visit(n.options.other);
    }
  }}
  visit(nodes);
  return {args:Object.fromEntries(Object.entries(args).sort()), tags:Object.fromEntries(Object.entries(tags).sort())};
}
export function validMessage(pattern, reference) {
  try {return JSON.stringify(signature(parseMessage(pattern))) === JSON.stringify(signature(parseMessage(reference)));} catch {return false;}
}
/** Text + semantic marks; never insert the result using innerHTML. */
export function formatMessageParts(pattern, values = {}, locale = 'en', reference = pattern, types = {}) {
  if (!validMessage(pattern, reference)) throw Error('Message signature mismatch');
  const ast = parseMessage(pattern), sig = signature(ast), parts = [];
  for (const name of Object.keys(sig.args)) {
    const value = values[name];
    if (!Object.hasOwn(values, name) || value === null || !['string','number'].includes(typeof value)) throw Error('Missing argument');
    if (sig.args[name] === 'number' || types[name] === 'integer') {if (typeof value !== 'number' || !Number.isSafeInteger(value)) throw Error('Expected integer');}
    if (types[name] === 'string' && typeof value !== 'string') throw Error('Expected display string');
  }
  const number = new Intl.NumberFormat(locale), rules = new Intl.PluralRules(locale);
  function render(list, marks = [], count) {for (const n of list) {
    if (n.kind === 'plural') {const v = values[n.name]; render(n.options[`=${v}`] || n.options[rules.select(v)] || n.options.other, marks, v);}
    else if (n.kind === 'tag') render(n.children, [...marks,n.name], count);
    else {const text = n.kind === 'text' ? n.value : n.kind === 'pound' ? number.format(count) : n.kind === 'number' ? number.format(values[n.name]) : String(values[n.name]); parts.push({text, bold:marks.includes('b'), underline:marks.includes('u')});}
  }}
  render(ast); return parts;
}
export function formatMessage(pattern, values = {}, locale = 'en', reference = pattern, types = {}) {
  return formatMessageParts(pattern, values, locale, reference, types).map(part => part.text).join('');
}
/** Opt-in contracts are separate from the legacy plain-text lookup, so raw ICU never reaches printf. */
export function messagePolicy(contracts = {}, localOnlyKeys = []) {
  const plain = plainTextPolicy(localOnlyKeys), fixed = new Set(localOnlyKeys);
  return (key, remote, local) => !fixed.has(key) && (contracts[key] ? validMessage(remote, contracts[key].template) : plain(key, remote, local));
}

export function plainTextPolicy(localOnlyKeys = []) {
  const excluded = new Set(localOnlyKeys);
  return (key, remote, local) => local !== undefined && !excluded.has(key)
    && !/[{}%<>]/.test(local) && !/[{}%<>]/.test(remote);
}
/** Faray public i18n delivery v1. No UI mutation and no business-response rewriting. */
export const locales = Object.freeze('en zh-Hans zh-Hant es fr de it pt ru ja ko ar hi id tr vi th nl pl ms'.split(' '));
export function normalizeLocale(value = 'en') {
  const tag = value.trim().replace(/_/g, '-').toLowerCase();
  if (/^zh-hans(?:-|$)/.test(tag)) return 'zh-Hans';
  if (/^zh-hant(?:-|$)/.test(tag)) return 'zh-Hant';
  if (/^zh(?:-|$)/.test(tag)) return /-(?:tw|hk|mo)(?:-|$)/.test(tag) ? 'zh-Hant' : 'zh-Hans';
  const primary = tag.split('-')[0] === 'in' ? 'id' : tag.split('-')[0];
  return locales.includes(primary) ? primary : 'en';
}
const MESSAGE_FORMAT = 'icu-mf1';
function requestURL(value) {
  const url = new URL(value); url.searchParams.set('messageFormat', MESSAGE_FORMAT); return url.href;
}
const LIMIT = 2097152;
const decode = bytes => new TextDecoder('utf-8', {fatal: true}).decode(bytes);
const flat = value => value && typeof value === 'object' && !Array.isArray(value) && Object.values(value).every(v => typeof v === 'string');
export async function sha256(bytes) {
  const digest = await globalThis.crypto.subtle.digest('SHA-256', bytes);
  return Array.from(new Uint8Array(digest), b => b.toString(16).padStart(2, '0')).join('');
}
export function validateManifest(m, endpoint, locale, platform = 'web') {
  const unified = typeof m?.version === 'string' && /^b1-[0-9]{17}-[0-9a-f]{8}$/.test(m.version);
  if (!m || m.schemaVersion !== 1 || (!unified && !/^s1-[0-9a-f]{64}$/.test(m.version)) ||
      (!unified && m.platform !== platform) || (m.platform !== undefined && m.platform !== platform) ||
      ((!unified || m.revision !== undefined) && (!Number.isSafeInteger(m.revision) || m.revision < 0))) throw Error('Invalid manifest');
  const d = m.packages?.[locale];
  const variant = {web:'web-icu-v1', android:'android-v1', ios:'ios-v1', flutter:'flutter-v1'}[platform];
  if (!d || !Number.isInteger(d.bytes) || d.bytes < 1 || d.bytes > LIMIT || !/^[0-9a-f]{64}$/.test(d.sha256)) throw Error('Invalid descriptor');
  const base = new URL(endpoint);
  const prefix = base.pathname.replace(/\/manifest$/, '/packages/');
  const expected = `${prefix}${m.version}/${variant}/${locale}.json`;
  const paths = [expected, ...(unified ? [`${prefix}${m.version}/${locale}.json`] : [])];
  if (!paths.some(path => d.url === path || d.url === `${path}?messageFormat=${MESSAGE_FORMAT}`)) throw Error('Unexpected package path');
  return {...d, absoluteURL: requestURL(new URL(d.url, base.origin).href)};
}
export async function validatePackage(bytes, descriptor) {
  if (bytes.length !== descriptor.bytes || await sha256(bytes) !== descriptor.sha256) throw Error('Package integrity failure');
  const entries = JSON.parse(decode(bytes));
  if (!flat(entries)) throw Error('Expected flat string map');
  return Object.freeze(entries);
}
async function request(url, etag, fetcher) {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), 15000);
  try {
    const response = await fetcher(url, {headers: etag ? {'If-None-Match':etag} : {}, credentials:'omit', redirect:'error', signal:controller.signal});
    if (response.status === 304) return {status:304};
    if (response.status !== 200) throw Error(`i18n HTTP ${response.status}`);
    if (Number(response.headers.get('Content-Length')) > LIMIT) throw Error('Response too large');
    const reader = response.body?.getReader();
    let bytes;
    if (reader) {
      const chunks = []; let size = 0;
      while (true) {
        const next = await reader.read(); if (next.done) break;
        size += next.value.length;
        if (size > LIMIT) { await reader.cancel(); throw Error('Response too large'); }
        chunks.push(next.value);
      }
      bytes = new Uint8Array(size); let offset = 0;
      for (const chunk of chunks) {bytes.set(chunk, offset); offset += chunk.length;}
    } else {
      bytes = new Uint8Array(await response.arrayBuffer());
      if (bytes.length > LIMIT) throw Error('Response too large');
    }
    return {status:200, bytes, etag:response.headers.get('ETag') || ''};
  } finally {clearTimeout(timer);}
}
/** Atomic localStorage envelope. Storage denial falls back to memory through the client. */
export const browserCache = {
  async read(key) {return globalThis.localStorage.getItem(key);},
  async write(key, value) {globalThis.localStorage.setItem(key, value);},
};
export class I18nClient {
  constructor({manifestURL, namespace, localMessages, locale = 'en', cache = browserCache, fetcher = globalThis.fetch, accept = () => true, contracts = {}, onError = () => {}}) {
    const url = new URL(manifestURL);
    if (!['https:', 'http:'].includes(url.protocol) || url.search || url.hash || url.username || url.password || !url.pathname.endsWith('/manifest')) throw Error('Invalid endpoint');
    if (!namespace) throw Error('Namespace required');
    this.endpoint = url.href; this.namespace = namespace; this.local = localMessages;
    this.locale = normalizeLocale(locale); this.cache = cache; this.fetcher = fetcher; this.accept = accept; this.onError = onError;
    this.contracts = contracts;
    this.listeners = new Set(); this.active = null; this.generation = 0; this.pending = Promise.resolve(); this.disposed = false;
  }
  subscribe(listener) {this.listeners.add(listener); return () => this.listeners.delete(listener);}
  emit() {for (const listener of this.listeners) {try {listener();} catch (error) {this.onError(error);}}}
  get messages() {
    const local = {...this.local('en'), ...this.local(this.locale)};
    for (const [key, value] of Object.entries(this.active?.entries || {})) {
      if (this.contracts[key] ? validMessage(value, this.contracts[key].template) : this.accept(key, value, local[key])) local[key] = value;
    }
    return local;
  }
  text(key, fallback = key) {return this.messages[key] ?? fallback;}
  formatParts(key, values = {}, fallback = key) {
    const contract = this.contracts[key];
    const local = this.local(this.locale)[key] ?? this.local('en')[key];
    const remote = this.active?.entries[key];
    if (contract) for (const pattern of [remote, local]) {
      if (typeof pattern !== 'string') continue;
      try {return formatMessageParts(pattern, values, this.locale, contract.template, contract.types);} catch { /* Fall back per key, never log arguments. */ }
    }
    return [{text:fallback, bold:false, underline:false}];
  }
  format(key, values = {}, fallback = key) {return this.formatParts(key, values, fallback).map(p => p.text).join('');}
  messageFromResponse(response) {
    const fallback = typeof response?.message === 'string' ? response.message : '';
    const key = response?.messageKey, values = response?.messageArgs ?? {};
    if (typeof key !== 'string' || !key.trim() || !this.contracts[key] ||
        !values || typeof values !== 'object' || Array.isArray(values)) return fallback;
    const types = this.contracts[key].types;
    if (Object.keys(types).sort().join('\n') !== Object.keys(values).sort().join('\n') ||
        Object.values(values).some(v => typeof v !== 'string' && (typeof v !== 'number' || !Number.isFinite(v)))) return fallback;
    return this.format(key, values, fallback) || fallback;
  }
  async start() {return this.setLocale(this.locale);}
  setLocale(value) {
    this.locale = normalizeLocale(value); const generation = ++this.generation;
    this.active = null; this.emit();
    return this.enqueue(async () => {
      if (!this.current(generation)) return;
      try {
        const raw = await this.cache.read(this.cacheKey());
        if (raw) {
          const envelope = JSON.parse(raw);
          const descriptor = validateManifest(envelope.manifest, this.endpoint, this.locale);
          if (envelope.identity !== this.cacheKey()) throw Error('Cache identity mismatch');
          const bytes = Uint8Array.from(atob(envelope.body), c => c.charCodeAt(0));
          const entries = await validatePackage(bytes, descriptor);
          if (this.current(generation)) {this.active = {...envelope, entries}; this.emit();}
        }
      } catch (error) {this.onError(error);}
      if (this.current(generation)) await this.synchronize(generation);
    });
  }
  cacheKey() {return `faray-i18n:v1:${this.namespace}:web:${this.endpoint}:${this.locale}:messageFormat=${MESSAGE_FORMAT}`;}
  current(generation) {return !this.disposed && generation === this.generation;}
  enqueue(action) {
    this.pending = this.pending.then(action).catch(error => {this.onError(error); return false;});
    return this.pending;
  }
  refresh() {const generation = this.generation; return this.enqueue(() => this.current(generation) && this.synchronize(generation));}
  async synchronize(generation) {
    const old = this.active;
    const response = await request(requestURL(this.endpoint), old?.etag, this.fetcher);
    if (!this.current(generation)) return false;
    if (response.status === 304) {if (!old) throw Error('304 without verified cache'); return false;}
    const manifest = JSON.parse(decode(response.bytes));
    const descriptor = validateManifest(manifest, this.endpoint, this.locale);
    let body, entries;
    if (old && old.manifest.version === manifest.version && old.manifest.packages[this.locale].sha256 === descriptor.sha256 && old.manifest.packages[this.locale].bytes === descriptor.bytes) {
      body = old.body; entries = old.entries;
    } else {
      const pack = await request(descriptor.absoluteURL, '', this.fetcher);
      if (pack.status !== 200) throw Error('Unexpected package response');
      entries = await validatePackage(pack.bytes, descriptor);
      let binary = ''; for (const byte of pack.bytes) binary += String.fromCharCode(byte);
      body = btoa(binary);
    }
    if (!this.current(generation)) return false;
    const envelope = {identity:this.cacheKey(), manifest, body, etag:response.etag};
    // Persist before exposing the new snapshot. A quota/write failure keeps the old package.
    await this.cache.write(this.cacheKey(), JSON.stringify(envelope));
    if (!this.current(generation)) return false;
    this.active = {...envelope, entries}; this.emit(); return true;
  }
  dispose() {this.disposed = true; ++this.generation; this.listeners.clear();}
}
